<template>
	<div class="shopping-order-page">
		<section class="page-heading">
			<div class="heading-copy">
				<span class="heading-icon"><i class="el-icon-shopping-bag-2"></i></span>
				<div>
					<h2>我的购物订单</h2>
					<p>查看当前账号购买的商城商品及企业展示服务订单</p>
				</div>
			</div>
			<div class="heading-total">
				<span>订单总数</span>
				<strong>{{ pageOptions.total }}</strong>
			</div>
		</section>

		<section class="order-filter-card">
			<div class="filter-caption">
				<span class="filter-icon"><i class="el-icon-search"></i></span>
				<div>
					<strong>筛选订单</strong>
					<small>按编号、类型及状态快速定位</small>
				</div>
			</div>
			<el-form :model="queryParams" class="order-search-form">
				<el-form-item>
					<el-input v-model.trim="queryParams.orderCode" clearable placeholder="请输入订单编号"></el-input>
				</el-form-item>
				<el-form-item>
					<el-select v-model="queryParams.orderType" clearable placeholder="全部订单类型">
						<el-option v-for="item in orderTypes" :key="item.value" :label="item.label" :value="item.value"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-select v-model="queryParams.auditState" clearable placeholder="全部审核状态">
						<el-option label="待财务审核" value="10"></el-option>
						<el-option label="待管理员审核" value="0"></el-option>
						<el-option label="审核通过" value="1"></el-option>
						<el-option label="审核不通过" value="2"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-select v-model="queryParams.orderState" clearable placeholder="全部订单状态">
						<el-option v-for="item in orderStatus" :key="item.value" :label="item.label" :value="item.value"></el-option>
					</el-select>
				</el-form-item>
				<div class="filter-actions">
					<el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
					<el-button icon="el-icon-refresh-left" @click="handleResetSearch">重置</el-button>
				</div>
			</el-form>
		</section>

		<section class="order-list-card">
			<div class="list-heading">
				<div>
					<strong>订单列表</strong>
					<span>仅展示当前账号作为购买人的订单</span>
				</div>
				<el-button size="small" icon="el-icon-refresh" @click="handleGetMyOrderList">刷新</el-button>
			</div>

			<div ref="orderList" v-loading="loading" class="order-list">
				<template v-if="orderList.length">
					<section v-for="group in groupedOrders" :key="group.key" class="shop-order-group">
						<header class="shop-group-header">
							<span class="shop-group-icon">
								<i :class="group.isGoods ? 'el-icon-office-building' : 'el-icon-data-analysis'"></i>
							</span>
							<div class="shop-group-copy">
								<strong>{{ group.shopName }}</strong>
								<span>{{ group.orders.length }} 笔订单</span>
							</div>
						</header>

						<article v-for="order in group.orders" :key="order.id" class="order-item">
							<header class="order-header">
								<div class="order-identification">
									<span :class="['order-type-tag', getOrderTypeClass(order.orderType)]">
										{{ getOrderTypeName(order.orderType) }}
									</span>
									<strong>订单号：{{ order.orderCode || '-' }}</strong>
									<span class="order-time">
										<i class="el-icon-time"></i>
										{{ order.createTime || '-' }}
									</span>
								</div>
								<div class="status-container">
									<span :class="['status-tag', getOrderStateClass(order.orderState)]">
										{{ getOrderStateName(order.orderState) }}
									</span>
									<span :class="['status-tag', getAuditStateClass(order)]">{{ getAuditStateName(order) }}</span>
									<span :class="['status-tag', getShippingState(order.shippingStatus).className]">
										{{ getShippingState(order.shippingStatus).text }}
									</span>
								</div>
							</header>

							<div class="order-content">
								<div class="order-products">
									<div
										v-for="(goods, goodsIndex) in getOrderGoods(order)"
										:key="goods.id || `${order.id}-${goodsIndex}`"
										class="product-info"
									>
										<el-image v-if="goods.mainImage" class="product-cover" :src="getGoodsImage(goods)" fit="cover">
											<div slot="error" class="cover-placeholder"><i class="el-icon-picture-outline"></i></div>
										</el-image>
										<div v-else class="product-cover cover-placeholder">
											<i :class="isGoodsOrder(order) ? 'el-icon-goods' : 'el-icon-data-analysis'"></i>
										</div>
										<div class="product-copy">
											<button
												v-if="canViewProductDetail(goods, order)"
												type="button"
												class="product-name-button"
												:title="getProductName(goods)"
												@click="handleViewProduct(goods, order)"
											>
												{{ getProductName(goods) }}
											</button>
											<strong v-else>{{ getProductName(goods) }}</strong>
											<span>{{ getProductDescription(goods, order) }}</span>
											<small v-if="!isGoodsOrder(order) && getProductSource(order)">
												<i class="el-icon-office-building"></i>
												{{ getProductSource(order) }}
											</small>
										</div>
										<div class="product-line-facts">
											<span>数量 × {{ goods.purchaseQuantity || 1 }}</span>
											<strong>¥ {{ formatAmount(goods.sellingPrice) }}</strong>
										</div>
									</div>
								</div>

								<div class="order-facts">
									<div class="fact-item">
										<span>支付方式</span>
										<strong>{{ getPaymentTypeName(order.paymentType) }}</strong>
									</div>
									<div class="fact-item amount-fact">
										<span>订单实付</span>
										<strong>¥ {{ formatAmount(order.actualPayment) }}</strong>
									</div>
								</div>
							</div>

							<footer class="order-footer">
								<div class="payment-meta">
									<div class="payment-time">
										<i class="el-icon-circle-check"></i>
										<span>{{ order.payTime ? `支付时间：${order.payTime}` : '订单尚未支付' }}</span>
									</div>
									<div
										v-if="shouldShowPaymentCountdown(order)"
										:class="['payment-countdown', { expired: isPaymentExpired(order) }]"
									>
										<i class="el-icon-time"></i>
										<span>
											{{ isPaymentExpired(order) ? '支付时间已结束' : `剩余支付时间 ${formatPaymentCountdown(order)}` }}
										</span>
									</div>
								</div>
								<div class="order-actions">
									<template v-if="isOrderUnpaid(order.orderState)">
										<template v-if="!isPaymentExpired(order)">
											<el-button type="primary" icon="el-icon-wallet" @click="handleOpenPayVouchaer(order)">立即付款</el-button>
											<el-button icon="el-icon-close" @click="handleCancelOrder(order)">取消订单</el-button>
										</template>
										<el-button v-else icon="el-icon-time" disabled>支付已超时</el-button>
									</template>
									<template v-else>
										<el-button icon="el-icon-document" @click="handleViewDetail(order)">订单详情</el-button>
										<el-button
											v-if="canApplyInvoice(order)"
											type="primary"
											plain
											icon="el-icon-document-checked"
											@click="handleApplyInvoice(order)"
										>
											申请开票
										</el-button>
										<el-button
											v-else-if="hasInvoiceApplication(order)"
											class="invoice-record-button"
											icon="el-icon-tickets"
											@click="handleViewInvoice(order)"
										>
											{{ getInvoiceActionName(order.invoiceState) }}
										</el-button>
										<el-button
											v-if="isDisplayOrder(order) && isMerchantRejected(getAuditState(order))"
											type="primary"
											plain
											icon="el-icon-edit-outline"
											@click="handleEditOrder(order)"
										>
											编辑资料
										</el-button>
										<el-popover
											v-model="order.deliveryProgressVisible"
											placement="left"
											width="360"
											trigger="click"
											popper-class="delivery-progress-popover"
										>
											<delivery-progress-card :order-id="order.id" :visible="order.deliveryProgressVisible" />
											<template #reference>
												<el-button icon="el-icon-truck">订单进度</el-button>
											</template>
										</el-popover>
									</template>
								</div>
							</footer>
						</article>
					</section>
				</template>

				<empty-content v-else empty-text="暂无购买订单"></empty-content>

				<div v-if="pageOptions.total > pageOptions.limit" class="pagination-container">
					<jr-pagination
						:limit="pageOptions.limit"
						:page.sync="pageOptions.page"
						:total="pageOptions.total"
						layout="prev, pager, next"
						prev-text="上一页"
						next-text="下一页"
						@change="handlePaginationChange"
					></jr-pagination>
				</div>
			</div>
		</section>

		<jr-dialog :visible.sync="payVoucherVisible" width="1400px" :show-btn="false" @close="handlePayClose">
			<template slot="title">
				<h4 class="model-title"><span>上传付款凭证</span></h4>
			</template>
			<template slot="content">
				<upload-pay-voucher
					v-if="payVoucherVisible"
					:order-id="curPayOrderId"
					:order-number="curPayOrderNumber"
					@uploadSuccess="handlePaySuccessCallback"
					@uploadCancel="handlePayClose"
				></upload-pay-voucher>
			</template>
		</jr-dialog>

		<jr-dialog :visible.sync="showDetailDialog" :show-btn="false" width="1074px" custom-class="order-detail-dialog">
			<template slot="title">
				<h4 class="order-title"><span>订单详情</span></h4>
			</template>
			<template v-if="showDetailDialog" slot="content">
				<order-detail :id="selectedOrderId" :order="selectedOrder"></order-detail>
			</template>
		</jr-dialog>
	</div>
</template>

<script>
	import JrDialog from '@/components/Dialog'
	import JrPagination from '@/components/Pagination'
	import UploadPayVoucher from '../market/components/uploadPayVoucher.vue'
	import OrderDetail from './components/orderDetail.vue'
	import DeliveryProgressCard from './components/DeliveryProgressCard.vue'
	import { cancelOrder } from '@/api/memberBenefit/index'
	import {
		cancelMerchantCheckout,
		previewMerchantFile,
		queryMerchantCheckoutDetail,
		queryMyPurchaseOrderPage
	} from '@/api/mall/index.js'
	import { ENUM_ORDER_STATE, ORDER_STATE, ORDER_STATE_CLASS, MERCHANT_ORDER_STATE } from '@/enum/index'

	const ORDER_TYPE_OPTIONS = [
		{ label: '企业宣传', value: 'MERCHANT_PUBLICITY' },
		{ label: '企业风采', value: 'MERCHANT_ELEGANCEANDCHARM' },
		{ label: '自营商品', value: 'MERCHANT_SELF_GOODS' },
		{ label: '展销商品', value: 'MERCHANT_EXHIBITION_GOODS' }
	]

	const createQueryParams = () => ({
		orderCode: '',
		orderType: '',
		orderState: '',
		auditState: ''
	})

	export default {
		name: 'ShoppManag',
		components: {
			JrDialog,
			JrPagination,
			UploadPayVoucher,
			OrderDetail,
			DeliveryProgressCard
		},
		data() {
			return {
				queryParams: createQueryParams(),
				pageOptions: {
					limit: 5,
					page: 1,
					total: 0
				},
				loading: false,
				currentTimestamp: Date.now(),
				countdownTimer: null,
				expiredSyncingPayOrderIds: [],
				orderTypes: ORDER_TYPE_OPTIONS,
				orderStatus: [],
				orderList: [],
				payVoucherVisible: false,
				curPayOrderId: '',
				curPayOrderNumber: '',
				showDetailDialog: false,
				selectedOrderId: '',
				selectedOrder: {}
			}
		},
		computed: {
			groupedOrders() {
				const groups = []
				const groupMap = new Map()
				this.orderList.forEach(order => {
					const isGoods = this.isGoodsOrder(order)
					const firstGoods = this.getOrderGoods(order)[0] || order
					const shopId = firstGoods.shopId || order.shopId || ''
					const shopName = isGoods ? firstGoods.shopName || order.shopName || '平台自营' : '企业展示服务'
					const key = isGoods ? `goods-${shopId || shopName}` : 'display-service'
					if (!groupMap.has(key)) {
						const group = { key, shopName, isGoods, orders: [] }
						groupMap.set(key, group)
						groups.push(group)
					}
					groupMap.get(key).orders.push(order)
				})
				return groups
			}
		},
		created() {
			// 从“我的主页”状态卡进入时，使用路由参数预设订单状态筛选项。
			if (this.$route.query.orderState !== undefined) {
				this.queryParams.orderState = String(this.$route.query.orderState)
			}
			this.orderStatus = Object.keys(ENUM_ORDER_STATE).map(key => ({
				label: ENUM_ORDER_STATE[key],
				value: key
			}))
			this.startCountdownTicker()
			this.handleGetMyOrderList()
		},
		beforeDestroy() {
			this.stopCountdownTicker()
		},
		methods: {
			getOrderStateName(state) {
				return ENUM_ORDER_STATE[state] || '未知状态'
			},
			getOrderStateClass(state) {
				return ORDER_STATE_CLASS[state] || 'is-default'
			},
			getOrderTypeName(type) {
				const option = ORDER_TYPE_OPTIONS.find(item => item.value === type)
				return option ? option.label : '商城订单'
			},
			getOrderTypeClass(type) {
				return {
					MERCHANT_PUBLICITY: 'is-publicity',
					MERCHANT_ELEGANCEANDCHARM: 'is-elegance',
					MERCHANT_SELF_GOODS: 'is-self',
					MERCHANT_EXHIBITION_GOODS: 'is-exhibition'
				}[type]
			},
			getAuditState(order) {
				const value = order.publicityStyleStatus
				return value === undefined || value === null || value === '' ? order.auditState : value
			},
			getAuditStateName(order) {
				return (
					{
						0: '待管理员审核',
						1: '审核通过',
						2: '审核不通过',
						10: '待财务审核'
					}[Number(this.getAuditState(order))] || '待审核'
				)
			},
			getAuditStateClass(order) {
				return (
					{
						0: 'waitaudit',
						1: 'passed',
						2: 'rejected',
						10: 'finance'
					}[Number(this.getAuditState(order))] || 'is-default'
				)
			},
			getShippingState(state) {
				return (
					{
						0: { text: '未发货', className: 'not-shipped' },
						1: { text: '已发货', className: 'shipped' },
						2: { text: '已收货', className: 'received' }
					}[Number(state)] || { text: '未发货', className: 'not-shipped' }
				)
			},
			getPaymentTypeName(type) {
				return (
					{
						WECHAT_PAY: '微信支付',
						ALIPAY: '支付宝',
						BANK_AGGREGATE: '聚合码支付'
					}[type] ||
					type ||
					'-'
				)
			},
			getOrderGoods(order) {
				if (Array.isArray(order.merchantGoodsList) && order.merchantGoodsList.length) {
					return order.merchantGoodsList
				}
				return [order]
			},
			getProductName(product) {
				return product.goodsTitle || product.displayTitle || product.eventName || product.setMealName || '商城服务'
			},
			canViewProductDetail(product, order) {
				return Boolean(this.isGoodsOrder(order) && (product.sourceGoodsId || order.sourceGoodsId))
			},
			getProductDetailRoute(product, order) {
				const goodsId = product.sourceGoodsId || order.sourceGoodsId
				if (!this.isGoodsOrder(order) || !goodsId) return null
				const goodsType = Number(product.goodsType || order.goodsType)
				return {
					name: goodsType === 2 ? 'MallStoreExhibitionGoodsDetail' : 'MallStoreSelfGoodsDetail',
					params: { id: goodsId }
				}
			},
			handleViewProduct(product, order) {
				const route = this.getProductDetailRoute(product, order)
				if (!route) {
					this.$modal.msgWarning('暂无可查看的商品详情')
					return
				}
				this.$router.push(route)
			},
			getProductDescription(product, order) {
				if (this.isGoodsOrder(order || product)) {
					return Number(product.goodsNature) === 2 ? '虚拟权益商品' : product.guideTitle || order.setMealName || '商城商品'
				}
				return product.setMealName || product.eventName || '企业展示服务'
			},
			getProductSource(order) {
				return order.shopName || order.companyName || ''
			},
			getGoodsImage(order) {
				const image = order.mainImage || ''
				if (!image || /^https?:\/\//.test(image) || image.indexOf('data:image') === 0) return image
				return previewMerchantFile() + image
			},
			formatAmount(value) {
				const amount = Number(value)
				return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
			},
			isGoodsOrder(order) {
				return order.businessType === 'GOODS' || ['MERCHANT_SELF_GOODS', 'MERCHANT_EXHIBITION_GOODS'].includes(order.orderType)
			},
			isDisplayOrder(order) {
				return !this.isGoodsOrder(order)
			},
			isOrderUnpaid(state) {
				return Number(state) === ORDER_STATE.UNPAID
			},
			isOrderPaid(order) {
				const state = Number(order && order.orderState)
				return Boolean(order && order.payTime && [ORDER_STATE.PAID, ORDER_STATE.WAITAUDIT].includes(state))
			},
			hasInvoiceApplication(order) {
				return Boolean(
					order &&
						(order.invoiceRecordId ||
							(order.invoiceState !== undefined && order.invoiceState !== null && order.invoiceState !== ''))
				)
			},
			canApplyInvoice(order) {
				return Boolean(this.isOrderPaid(order) && !this.hasInvoiceApplication(order))
			},
			getInvoiceActionName(state) {
				return Number(state) === 1 ? '查看发票' : '查看开票申请'
			},
			shouldShowPaymentCountdown(order) {
				return Boolean(this.isGoodsOrder(order) && order.payOrderId && order.loseTime && this.isOrderUnpaid(order.orderState))
			},
			getPaymentExpireTimestamp(order) {
				if (!order || !order.loseTime) return 0
				if (typeof order.loseTime === 'number') {
					return String(Math.trunc(order.loseTime)).length === 10 ? order.loseTime * 1000 : order.loseTime
				}
				const value = String(order.loseTime).trim()
				const normalized = value.replace(/^(\d{4})-(\d{2})-(\d{2})/, '$1/$2/$3')
				const timestamp = new Date(normalized).getTime()
				return Number.isFinite(timestamp) ? timestamp : 0
			},
			getRemainingPaymentSeconds(order) {
				const expireTimestamp = this.getPaymentExpireTimestamp(order)
				if (!expireTimestamp) return 0
				return Math.max(0, Math.ceil((expireTimestamp - this.currentTimestamp) / 1000))
			},
			isPaymentExpired(order) {
				return this.shouldShowPaymentCountdown(order) && this.getRemainingPaymentSeconds(order) <= 0
			},
			formatPaymentCountdown(order) {
				const seconds = this.getRemainingPaymentSeconds(order)
				const hours = Math.floor(seconds / 3600)
				const minutes = Math.floor((seconds % 3600) / 60)
				const restSeconds = seconds % 60
				const minuteText = String(minutes).padStart(2, '0')
				const secondText = String(restSeconds).padStart(2, '0')
				return hours > 0 ? `${String(hours).padStart(2, '0')}:${minuteText}:${secondText}` : `${minuteText}:${secondText}`
			},
			startCountdownTicker() {
				this.stopCountdownTicker()
				this.currentTimestamp = Date.now()
				this.countdownTimer = window.setInterval(() => {
					this.currentTimestamp = Date.now()
					this.syncExpiredPaymentOrders()
				}, 1000)
			},
			stopCountdownTicker() {
				if (!this.countdownTimer) return
				window.clearInterval(this.countdownTimer)
				this.countdownTimer = null
			},
			async syncExpiredPaymentOrders() {
				const payOrderIds = [
					...new Set(
						this.orderList
							.filter(order => this.isPaymentExpired(order))
							.map(order => order.payOrderId)
							.filter(Boolean)
					)
				].filter(payOrderId => !this.expiredSyncingPayOrderIds.includes(payOrderId))
				if (!payOrderIds.length) return
				this.expiredSyncingPayOrderIds.push(...payOrderIds)
				try {
					await Promise.all(payOrderIds.map(payOrderId => queryMerchantCheckoutDetail({ payOrderId }).catch(() => null)))
					await this.handleGetMyOrderList()
				} finally {
					this.expiredSyncingPayOrderIds = this.expiredSyncingPayOrderIds.filter(id => !payOrderIds.includes(id))
				}
			},
			isMerchantRejected(state) {
				return Number(state) === MERCHANT_ORDER_STATE.REJECTED
			},
			async handleGetMyOrderList() {
				this.loading = true
				try {
					const response = await queryMyPurchaseOrderPage({
						page: this.pageOptions.page,
						size: this.pageOptions.limit,
						...this.queryParams
					})
					const data = response.data || {}
					this.orderList = (data.records || []).map(item => ({
						...item,
						deliveryProgressVisible: false
					}))
					this.pageOptions.total = Number(data.total || 0)
					// 查询和翻页后从首条订单开始展示，避免沿用上一次滚动位置。
					this.$nextTick(() => {
						if (this.$refs.orderList) this.$refs.orderList.scrollTop = 0
					})
				} catch (error) {
					this.orderList = []
					this.pageOptions.total = 0
				} finally {
					this.loading = false
				}
			},
			handlePaginationChange(pager) {
				this.pageOptions.page = pager.page
				this.pageOptions.limit = pager.limit
				this.handleGetMyOrderList()
			},
			handleSearch() {
				this.pageOptions.page = 1
				this.handleGetMyOrderList()
			},
			handleResetSearch() {
				this.queryParams = createQueryParams()
				this.pageOptions.page = 1
				this.pageOptions.limit = 5
				this.handleGetMyOrderList()
			},
			handleViewDetail(order) {
				this.selectedOrderId = order.id
				this.selectedOrder = { ...order }
				this.showDetailDialog = true
			},
			handleApplyInvoice(order) {
				this.$router.push({
					path: '/user/market/order/select',
					query: { orderId: order.id, autoApply: '1' }
				})
			},
			handleViewInvoice() {
				this.$router.push('/user/invoiceManagement/myInvoice')
			},
			handleEditOrder(order) {
				this.$router.push({
					path: '/mallStore/purchase',
					query: {
						displaySectorType: order.orderType === 'MERCHANT_PUBLICITY' ? '1' : '2',
						orderId: order.id
					}
				})
			},
			handleCancelOrder(order) {
				this.$modal
					.confirm('请确认是否取消该订单？')
					.then(() =>
						this.isGoodsOrder(order) && order.payOrderId
							? cancelMerchantCheckout({ payOrderId: order.payOrderId })
							: cancelOrder({ orderId: order.id })
					)
					.then(() => {
						this.$modal.msgSuccess('订单取消成功')
						this.handleGetMyOrderList()
					})
					.catch(() => {})
			},
			handleOpenPayVouchaer(order) {
				if (this.isGoodsOrder(order) && order.payOrderId) {
					this.$router.push({ name: 'MallStorePayment', query: { payOrderId: order.payOrderId } })
					return
				}
				this.curPayOrderId = order.id
				this.curPayOrderNumber = order.orderCode
				this.payVoucherVisible = true
			},
			handlePayClose() {
				this.curPayOrderId = ''
				this.curPayOrderNumber = ''
				this.payVoucherVisible = false
			},
			handlePaySuccessCallback() {
				this.handlePayClose()
				this.handleGetMyOrderList()
			}
		}
	}
</script>

<style lang="scss" scoped>
	.shopping-order-page {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		padding: 12px 16px;
		overflow: hidden;
		color: #344154;
		background: #f5f8fb;
		box-sizing: border-box;
	}

	.page-heading,
	.order-filter-card,
	.order-list-card {
		border: 1px solid #e2e8ef;
		border-radius: 8px;
		background: #fff;
	}

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 16px;
	}

	.heading-copy {
		display: flex;
		align-items: center;
		gap: 10px;

		h2 {
			margin: 0 0 3px;
			color: #303b4c;
			font-size: 18px;
		}

		p {
			margin: 0;
			color: #8b96a5;
			font-size: 13px;
		}
	}

	.heading-icon,
	.filter-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 7px;
		color: #2671cb;
		background: #edf5fe;
	}

	.heading-icon {
		width: 34px;
		height: 34px;
		font-size: 18px;
	}

	.heading-total {
		display: flex;
		align-items: baseline;
		gap: 8px;
		padding: 5px 11px;
		border: 1px solid #dce8f5;
		border-radius: 6px;
		color: #728093;
		background: #f7fafe;

		strong {
			color: #2671cb;
			font-size: 19px;
		}
	}

	.order-filter-card {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 10px;
		padding: 8px 12px;
	}

	.filter-caption {
		display: flex;
		flex: 0 0 160px;
		align-items: center;
		gap: 8px;
		padding-right: 12px;
		border-right: 1px solid #e6ebf1;

		.filter-icon {
			width: 30px;
			height: 30px;
			font-size: 15px;
		}

		strong,
		small {
			display: block;
		}

		strong {
			font-size: 14px;
		}

		small {
			display: none;
			color: #9aa4b0;
			font-size: 11px;
		}
	}

	.order-search-form {
		display: grid;
		flex: 1;
		grid-template-columns: minmax(170px, 1.2fr) repeat(3, minmax(150px, 1fr)) auto;
		align-items: center;
		gap: 8px;

		::v-deep .el-form-item {
			margin-bottom: 0;
		}

		::v-deep .el-select {
			width: 100%;
		}

		::v-deep .el-input__inner {
			height: 32px;
			border-color: #dfe6ee;
			border-radius: 6px;
			line-height: 32px;
		}

		::v-deep .el-input__icon {
			line-height: 32px;
		}
	}

	.filter-actions {
		display: flex;
		white-space: nowrap;

		.el-button {
			height: 32px;
			padding: 0 13px;
		}
	}

	.order-list-card {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		margin-top: 10px;
		padding: 12px 14px;
		overflow: hidden;
	}

	.list-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
		padding-bottom: 9px;
		border-bottom: 1px solid #e7ecf2;

		strong {
			font-size: 16px;
		}

		span {
			margin-left: 10px;
			color: #98a2af;
			font-size: 12px;
		}
	}

	.shop-order-group {
		margin-bottom: 18px;
		border: 1px solid #dce5ee;
		border-radius: 8px;
		background: #f7fafc;
	}

	.shop-group-header {
		display: flex;
		align-items: center;
		gap: 11px;
		min-height: 54px;
		padding: 8px 15px;
		border-bottom: 1px solid #dce5ee;
		border-left: 4px solid #278167;
		background: #f0f7f4;
		box-sizing: border-box;
	}

	.shop-group-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border-radius: 6px;
		color: #fff;
		font-size: 17px;
		background: #278167;
	}

	.shop-group-copy {
		display: flex;
		align-items: baseline;
		gap: 10px;

		strong {
			color: #1e3d36;
			font-size: 18px;
		}

		span {
			color: #71837e;
			font-size: 12px;
		}
	}

	.order-item {
		margin: 12px;
		overflow: hidden;
		border: 1px solid #e2e8ef;
		border-radius: 8px;
		background: #fff;
		transition: border-color 0.2s, box-shadow 0.2s;

		&:hover {
			border-color: #bfd5ec;
			box-shadow: 0 7px 20px rgba(42, 78, 114, 0.08);
		}
	}

	.order-list {
		flex: 1;
		min-height: 0;
		padding-right: 5px;
		overflow-x: hidden;
		overflow-y: auto;
		scrollbar-color: #b8cce0 #edf2f7;
		scrollbar-width: thin;

		&::-webkit-scrollbar {
			width: 7px;
		}

		&::-webkit-scrollbar-track {
			border-radius: 4px;
			background: #edf2f7;
		}

		&::-webkit-scrollbar-thumb {
			border-radius: 4px;
			background: #b8cce0;

			&:hover {
				background: #91afd0;
			}
		}
	}

	.order-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		padding: 11px 16px;
		border-bottom: 1px solid #e8edf3;
		background: #f8fafc;
	}

	.order-identification,
	.status-container {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.order-identification {
		strong {
			color: #3b485a;
			font-size: 13px;
		}

		.order-time {
			color: #8b96a4;
			font-size: 12px;

			i {
				margin-right: 5px;
			}
		}
	}

	.order-type-tag,
	.status-tag {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 25px;
		padding: 0 9px;
		border-radius: 6px;
		font-size: 12px;
		white-space: nowrap;
		box-sizing: border-box;
	}

	.order-type-tag {
		color: #2872bd;
		background: #e9f3fd;

		&.is-elegance {
			color: #7654a8;
			background: #f1ebf9;
		}

		&.is-self {
			color: #287b62;
			background: #e6f5ee;
		}

		&.is-exhibition {
			color: #b06c1b;
			background: #fff1dc;
		}
	}

	.status-tag {
		color: #6f7b89;
		background: #edf0f3;

		&.unpaid,
		&.waitaudit,
		&.not-shipped {
			color: #a97020;
			background: #fff2dc;
		}

		&.success,
		&.passed,
		&.shipped {
			color: #168157;
			background: #e6f5ed;
		}

		&.finance {
			color: #3970b7;
			background: #e8f1fd;
		}

		&.rejected,
		&.abolish {
			color: #c94d4d;
			background: #fdeaea;
		}

		&.cancelled,
		&.received,
		&.is-default {
			color: #6f7b89;
			background: #edf0f3;
		}
	}

	.order-content {
		display: grid;
		grid-template-columns: minmax(420px, 1fr) auto;
		align-items: center;
		gap: 24px;
		padding: 17px 16px;
	}

	.order-products {
		min-width: 0;
	}

	.product-info {
		display: flex;
		align-items: center;
		min-width: 0;

		& + .product-info {
			margin-top: 12px;
			padding-top: 12px;
			border-top: 1px solid #e5ebf1;
		}
	}

	.product-cover {
		display: flex;
		flex: 0 0 78px;
		align-items: center;
		justify-content: center;
		width: 78px;
		height: 62px;
		margin-right: 13px;
		overflow: hidden;
		border: 1px solid #dfe7ef;
		border-radius: 7px;
		background: #f1f6fb;
	}

	.cover-placeholder {
		color: #77a6d5;
		font-size: 25px;
	}

	.product-copy {
		flex: 1;
		min-width: 0;

		strong,
		span,
		small {
			display: block;
		}

		strong {
			overflow: hidden;
			color: #303c4d;
			font-size: 16px;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.product-name-button {
			display: block;
			max-width: 100%;
			padding: 0;
			overflow: hidden;
			border: 0;
			color: #303c4d;
			font-family: inherit;
			font-size: 16px;
			font-weight: 600;
			line-height: 1.4;
			text-align: left;
			text-overflow: ellipsis;
			white-space: nowrap;
			background: transparent;
			cursor: pointer;
			transition: color 0.2s ease;

			&:hover {
				color: #2671cb;
			}

			&:focus {
				border-radius: 2px;
				color: #2671cb;
				outline: 2px solid rgba(38, 113, 203, 0.25);
				outline-offset: 2px;
			}
		}

		span {
			margin-top: 6px;
			color: #7e8997;
			font-size: 13px;
		}

		small {
			margin-top: 6px;
			color: #99a3af;
			font-size: 12px;

			i {
				margin-right: 5px;
			}
		}
	}

	.product-line-facts {
		display: flex;
		flex: 0 0 108px;
		flex-direction: column;
		align-items: flex-end;
		gap: 7px;
		margin-left: 16px;

		span {
			color: #7d8997;
			font-size: 12px;
		}

		strong {
			color: #d75537;
			font-size: 15px;
		}
	}

	.order-facts {
		display: flex;
		align-items: stretch;
	}

	.fact-item {
		display: flex;
		flex-direction: column;
		justify-content: center;
		min-width: 100px;
		padding: 0 18px;
		border-left: 1px solid #e7ecf2;
		text-align: center;

		span {
			margin-bottom: 7px;
			color: #929ca9;
			font-size: 12px;
		}

		strong {
			color: #3e4a5b;
			font-size: 14px;
		}
	}

	.amount-fact strong {
		color: #dc5b39;
		font-size: 18px;
	}

	.order-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 16px;
		border-top: 1px solid #e8edf3;
		background: #fbfcfd;
	}

	.payment-time {
		color: #8994a2;
		font-size: 12px;

		i {
			margin-right: 6px;
			color: #69a2db;
		}
	}

	.payment-meta {
		display: flex;
		align-items: center;
		gap: 14px;
		min-width: 0;
	}

	.payment-countdown {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		min-height: 28px;
		padding: 0 9px;
		border: 1px solid #f1cf9b;
		border-radius: 6px;
		color: #a66312;
		font-size: 12px;
		font-weight: 600;
		background: #fff7e8;
		box-sizing: border-box;
		white-space: nowrap;

		&.expired {
			border-color: #e5c0c0;
			color: #b94b4b;
			background: #fff1f1;
		}
	}

	.order-actions {
		display: flex;
		align-items: center;

		> * + * {
			margin-left: 9px;
		}

		.el-button {
			padding: 8px 13px;
			border-radius: 6px;
		}
	}

	.pagination-container {
		display: flex;
		justify-content: center;
		padding-top: 8px;
	}

	@media (max-width: 1280px) {
		.order-filter-card {
			align-items: flex-start;
		}

		.order-search-form {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.filter-actions {
			justify-content: flex-end;
			grid-column: 2;
		}
	}

	@media (max-width: 860px) {
		.shopping-order-page {
			padding: 14px;
			overflow-y: auto;
		}

		.order-filter-card,
		.order-header,
		.order-footer {
			align-items: flex-start;
			flex-direction: column;
		}

		.payment-meta {
			flex-wrap: wrap;
		}

		.filter-caption {
			width: 100%;
			padding: 0 0 10px;
			border-right: 0;
			border-bottom: 1px solid #e6ebf1;
		}

		.order-search-form,
		.order-content {
			width: 100%;
			grid-template-columns: 1fr;
		}

		.filter-actions {
			justify-content: flex-start;
			grid-column: 1;
		}

		.order-identification,
		.status-container {
			flex-wrap: wrap;
		}

		.order-facts {
			width: 100%;
		}

		.order-list-card,
		.order-list {
			overflow: visible;
		}

		.fact-item:first-child {
			border-left: 0;
		}
	}
</style>

<style lang="scss">
	.delivery-progress-popover {
		padding: 0 !important;
		border: 1px solid #9ec0e4;
	}

	.order-detail-dialog {
		overflow: hidden;
		border: 1px solid #c7d9ec;
		border-radius: 8px;
		box-shadow: 0 12px 34px rgba(39, 80, 140, 0.16);

		.el-dialog__headerbtn {
			top: 10px;
			right: 16px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			background: transparent;
			border-radius: 4px;

			.el-dialog__close {
				color: #667386;
				font-size: 20px;
				font-weight: 600;
			}

			&:hover {
				background: #eef2f6;

				.el-dialog__close {
					color: #263548;
				}
			}
		}

		.order-title {
			margin: 0;
			padding: 12px 48px 13px 0;
			border-bottom: 1px solid #e2e8ef;

			span {
				color: #344154;
				font-size: 18px;
			}
		}
	}
</style>
